'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CountingNumber, MotionHighlight } from '@/components/animate-ui'
import { cn } from '@/lib/utils'
import { LucideIcon } from 'lucide-react'

interface AnimatedStatCardProps {
  title: string
  value: string | number
  change?: string
  trend?: 'up' | 'down'
  icon: LucideIcon
  description?: string
  className?: string
  delay?: number
  isNumber?: boolean
  decimals?: number
  prefix?: string
  suffix?: string
}

export default function AnimatedStatCard({
  title,
  value,
  change,
  trend,
  icon: Icon,
  description,
  className,
  delay = 0,
  isNumber = true,
  decimals = 0,
  prefix = '',
  suffix = '',
}: AnimatedStatCardProps) {
  const numericValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay,
        ease: 'easeOut',
      }}
    >
      <MotionHighlight
        className={cn('h-full', className)}
        highlightClassName="bg-primary/5 border border-primary/20"
      >
        <Card className="border-0 shadow-sm transition-shadow duration-300 hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {title}
            </CardTitle>
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ duration: 0.2 }}
            >
              <Icon className="h-4 w-4 text-muted-foreground" />
            </motion.div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isNumber && typeof numericValue === 'number' && !isNaN(numericValue) ? (
                <CountingNumber
                  value={numericValue}
                  decimals={decimals}
                  prefix={prefix}
                  suffix={suffix}
                />
              ) : (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: delay + 0.2 }}
                >
                  {value}
                </motion.span>
              )}
            </div>
            {(change || description) && (
              <motion.p
                className="text-xs text-muted-foreground"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: delay + 0.4 }}
              >
                {change && (
                  <motion.span
                    className={cn(
                      trend === 'up' ? 'text-green-600' : 'text-red-600'
                    )}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    {change}
                  </motion.span>
                )}
                {change && description && ' '}
                {description}
              </motion.p>
            )}
          </CardContent>
        </Card>
      </MotionHighlight>
    </motion.div>
  )
}